import { useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@stores/gameStore';

interface MilestoneConfig {
  threshold: number;
  color: string;
  glowColor: string;
  label: string;
}

const MILESTONES: MilestoneConfig[] = [
  { threshold: 10, color: '#dc2626', glowColor: '#dc262680', label: 'MEGA' },
  { threshold: 5, color: '#f97316', glowColor: '#f9731680', label: 'SUPER' },
  { threshold: 2, color: '#22c55e', glowColor: '#22c55e80', label: 'NICE' },
  { threshold: 1, color: '#fbbf24', glowColor: '#fbbf2480', label: '' },
];

/**
 * Multiplier display component
 * Shows current multiplier with color transitions and milestone effects
 */
export function MultiplierDisplay(): React.JSX.Element {
  const { multiplier, gamePhase, crashMultiplier, roundId } = useGameStore();
  const [showMilestone, setShowMilestone] = useState(false);
  const [lastMilestone, setLastMilestone] = useState(0);

  // Determine current milestone
  const currentMilestone = useMemo(() => {
    for (const milestone of MILESTONES) {
      if (multiplier >= milestone.threshold) {
        return milestone;
      }
    }
    // Default milestone - array length is always >= 1
    const defaultMilestone = MILESTONES[MILESTONES.length - 1];
    return defaultMilestone ?? MILESTONES[0] ?? { threshold: 1, color: '#fbbf24', glowColor: '#fbbf2480', label: '' };
  }, [multiplier]);

  // Track milestone changes
  useEffect(() => {
    const milestoneThreshold = currentMilestone.threshold;
    if (milestoneThreshold > lastMilestone && milestoneThreshold > 1) {
      setShowMilestone(true);
      setLastMilestone(milestoneThreshold);
      const timer = setTimeout(() => {
        setShowMilestone(false);
      }, 1500);
      return (): void => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [currentMilestone.threshold, lastMilestone]);

  // Reset on new round
  useEffect(() => {
    if (gamePhase === 'betting') {
      setLastMilestone(0);
    }
  }, [gamePhase]);

  // Crashed state
  if (gamePhase === 'crashed') {
    return (
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative"
        >
          {/* Explosion effect */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="text-8xl">FLEW AWAY!</span>
          </motion.div>

          {/* Crashed text */}
          <div className="mt-16 rounded-lg bg-aviator-danger/90 px-8 py-4 backdrop-blur-sm">
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 0.5 }}
              className="text-center"
            >
              <p className="text-2xl font-bold text-white">FLEW AWAY AT</p>
              <p className="text-5xl font-bold text-white">
                {crashMultiplier?.toFixed(2) ?? '0.00'}x
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Round ID */}
        {roundId && (
          <p className="mt-2 text-sm text-white/60">Round: {roundId.slice(0, 8)}...</p>
        )}
      </div>
    );
  }

  // Waiting state
  if (gamePhase === 'waiting') {
    return (
      <div className="flex flex-col items-center">
        <div className="rounded-lg bg-aviator-skyDark/80 px-8 py-4 backdrop-blur-sm">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <p className="text-3xl font-bold text-white/80">STARTING SOON...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Betting state
  if (gamePhase === 'betting') {
    return (
      <div className="flex flex-col items-center">
        <div className="rounded-lg bg-aviator-skyDark/80 px-8 py-4 backdrop-blur-sm">
          <motion.div
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <p className="text-2xl font-bold text-aviator-gold">PLACE YOUR BETS</p>
            <p className="mt-2 text-center text-4xl font-bold text-white">1.00x</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Milestone popup */}
      <AnimatePresence>
        {showMilestone && currentMilestone.label && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            className="mb-2 text-2xl font-bold"
            style={{ color: currentMilestone.color }}
          >
            {currentMilestone.label}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main multiplier display */}
      <motion.div
        animate={
          showMilestone
            ? {
                scale: [1, 1.2, 1],
                boxShadow: [
                  `0 0 0px ${currentMilestone.glowColor}`,
                  `0 0 40px ${currentMilestone.glowColor}`,
                  `0 0 20px ${currentMilestone.glowColor}`,
                ],
              }
            : { scale: 1 }
        }
        transition={{ duration: 0.5 }}
        className="rounded-lg bg-aviator-skyDark/90 px-8 py-4 backdrop-blur-sm"
        style={{
          boxShadow: `0 0 20px ${currentMilestone.glowColor}`,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={Math.floor(multiplier * 10)}
            initial={{ scale: 1.1, opacity: 0.8 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="text-center"
          >
            <span
              className="text-6xl font-bold"
              style={{
                color: currentMilestone.color,
                textShadow: `0 0 10px ${currentMilestone.glowColor}`,
              }}
            >
              {multiplier.toFixed(2)}x
            </span>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Round ID */}
      {roundId && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 text-sm text-white/60"
        >
          Round: {roundId.slice(0, 8)}...
        </motion.p>
      )}
    </div>
  );
}
